// Central session lifecycle for the admin portal.
//
// The passwordless Cognito idToken (sessionStorage 'token') and the provider-scoped adminToken both
// carry a ~1h lifetime. Two independent triggers end a session and return the user to the login page:
//   1. Inactivity — no user interaction for SESSION_INACTIVITY_LIMIT_MS (see useInactivityLogout).
//   2. Token expiry / rejection — any admin API 401/403 (handleAuthFailure), or a decodable idToken
//      whose exp has already passed (isSessionValid).
// Both converge on endSession(): clear the stored session and hard-redirect to login. This replaces
// the previous behaviour where an expired token surfaced as a raw 4xx on the next action (e.g.
// changing a dashboard filter) while the UI still looked functional.

export const SESSION_INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hour

const LOGIN_PATH = "/";

export function getSessionToken(): string | null {
  return sessionStorage.getItem("token");
}

// Decode a JWT payload's `exp` (ms since epoch); null if absent or undecodable.
function jwtExpiryMs(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

// A session is valid when a Cognito idToken is present and — if its exp is decodable — not yet past.
// An undecodable token is trusted on presence (the API 401 handler + inactivity timer are the
// backstop), so a parse quirk never locks a working session out.
export function isSessionValid(nowMs: number = Date.now()): boolean {
  const token = getSessionToken();
  if (!token) return false;
  const expiryMs = jwtExpiryMs(token);
  return expiryMs === null || expiryMs > nowMs;
}

let ending = false;

// Clear the stored session and send the user to the login page. Guarded so the many parallel API
// calls that all 401 at once — and being already on the login page — don't loop or thrash. The
// session is always cleared (idempotent); only the redirect is guarded.
export function endSession(): void {
  sessionStorage.clear();
  if (ending || window.location.pathname === LOGIN_PATH) return;
  ending = true;
  window.location.assign(LOGIN_PATH);
}

// Called by every admin API client on a response error: a 401/403 means the token has expired or
// been rejected, so end the session. Other statuses are left for the caller to handle.
export function handleAuthFailure(status: number | undefined): void {
  if (status === 401 || status === 403) endSession();
}
